import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { FileData } from '@ui/components/FileUpload/types'
import { CheckIcon } from '@ui/icons/Check.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { FormSubmitValues } from '@utils/formValidation'
import { trimToMaxLength } from '@utils/validations'

import { BankAccountValidationOptions } from '@model/BankAccount'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'
import { isDocumentStatusApproved } from '@model/DocumentStatus'

import { createFormField } from '@global/formField/FormField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { scrollToTop, useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'
import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'

import { CreateBankAccountFormValues } from './CreateBankAccountPage'

import styles from './CreateBankAccountForm.module.scss'

interface OuterProps {
  onSubmit(values: FormSubmitValues<CreateBankAccountFormValues>): Promise<void>
  onCancelNewBank(): void
  documents: DocumentClientAreaDto[]
  validationRules?: BankAccountValidationOptions
}

const DOCUMENTS_LIMIT = 3

const FormField = createFormField<Omit<CreateBankAccountFormValues, 'document'>>()

const CreateBankAccountFormUI: React.FC<FormikProps<CreateBankAccountFormValues> & OuterProps> = (
  props
) => {
  useScrollToTop()

  const { isValid, isSubmitting, errors, values, touched, documents } = props
  const { handleSubmit, setSubmitting, setValues, setFieldTouched, onCancelNewBank } = props

  const { t } = useTranslation()
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
  }, [])

  const [areAllDocumentsShown, setAreAllDocumentsShown] = useState(false)
  const cancelModal = useModal(ActionModal, {
    title: t('Cancel Adding New Bank Account?'),
    description: t('Do you really like to cancel the Adding New Bank Account?'),
    onConfirm: onCancelNewBank,
    onClose: () => {
      scrollToTop()
    },
  })
  const slicedDocuments = useMemo(() => {
    return areAllDocumentsShown ? documents : documents.slice(0, DOCUMENTS_LIMIT)
  }, [areAllDocumentsShown, documents])

  const handleSubmitForm = (event: SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  const handleUploadDocumentField = (doc?: FileData) => {
    setFieldTouched('document', true)
    if (!doc) {
      setValues((values) => ({ ...values, document: undefined }))
    } else {
      setValues((values) => ({ ...values, document: doc, previouslyUploadedDocumentId: undefined }))
    }
  }

  const isUploadedDocumentsSown = !!slicedDocuments.length && !values.document
  const isDocumentFieldsTouched = touched.document || touched.previouslyUploadedDocumentId

  return (
    <React.Fragment>
      <Loading showLoadingIcon isLoading={false}>
        <FormTemplate title={t('New Bank Account')} goBack={() => cancelModal.open()}>
          <Form className={styles.form} onSubmit={handleSubmitForm}>
            <FieldWrapper>
              <FormField name='bankName' label={t('Name of Bank')} required maxlength={255} />
              <FormField name='bankAddress' label={t('Bank Address')} required maxlength={255} />
              <FormField
                name='accountName'
                label={t('Beneficiary Name')}
                required
                maxlength={128}
              />
              <FormField
                name='bankAccount'
                label={t('IBAN/Account Number')}
                required
                maxlength={64}
                type={'text'}
              />
              <FormField name='bankCode' label={t('BIC / Swift Code')} required maxlength={32} />
              <FormField name='codeSort' label={t('Branch Number/Sort Code')} maxlength={32} />
            </FieldWrapper>
            <div className={styles.uploadSection}>
              <div className={styles.label}>
                <Body2 medium>{t('Bank Statement') + ' *'}</Body2>
              </div>
              {!!errors.document && isDocumentFieldsTouched && (
                <span className={styles.error}>{errors.document}</span>
              )}

              {isUploadedDocumentsSown && (
                <div className={styles.docList}>
                  {slicedDocuments.map((document) => (
                    <Box flex gap='x8' justify='space-between' fullWidth key={document.id}>
                      <Checkbox
                        checked={values.previouslyUploadedDocumentId === document.id}
                        onChange={() => {
                          setFieldTouched('previouslyUploadedDocumentId', true, true)
                          setValues((values) => ({
                            ...values,
                            document: undefined,
                            previouslyUploadedDocumentId:
                              values.previouslyUploadedDocumentId === document.id
                                ? undefined
                                : document.id,
                          }))
                        }}
                      >
                        {document.name}
                      </Checkbox>
                      {isDocumentStatusApproved(document.status.id) && (
                        <CheckIcon color='text-success-high-em' size={24} />
                      )}
                    </Box>
                  ))}
                </div>
              )}

              {isUploadedDocumentsSown && documents.length > DOCUMENTS_LIMIT && (
                <Box pt='x8' pb='x8'>
                  <Button.Ghost
                    iconLeft={areAllDocumentsShown ? ChevronIcon.Up : ChevronIcon.Down}
                    onClick={() => setAreAllDocumentsShown((prevState) => !prevState)}
                  >
                    {areAllDocumentsShown ? t('Show less') : t('Show more')}
                  </Button.Ghost>
                </Box>
              )}

              {!values.previouslyUploadedDocumentId?.length && (
                <>
                  <Body2>
                    {t('Provide a clear high-quality unobstructed image of your bank statement')}
                  </Body2>
                  <DocumentFileUpload
                    file={values.document}
                    categorySizes={categorySizes}
                    documentCategory={DocumentCategoryType.Personal}
                    onRemove={() => handleUploadDocumentField(undefined)}
                    onUpload={handleUploadDocumentField}
                  />
                </>
              )}
            </div>

            <TransactionFormActionButtonTemplate>
              <Button.Secondary size='lg' onClick={() => cancelModal.open()}>
                {t('Cancel')}
              </Button.Secondary>
              <Button.Primary size='lg' disabled={!isValid || isSubmitting} type='submit'>
                {t('Confirm')}
              </Button.Primary>
            </TransactionFormActionButtonTemplate>
          </Form>
        </FormTemplate>
      </Loading>
    </React.Fragment>
  )
}

const enforceMaxLength = (values: CreateBankAccountFormValues): CreateBankAccountFormValues => {
  return {
    ...values,
    bankName: values.bankName ? trimToMaxLength(values.bankName, 255) : values.bankName,
    bankAddress: values.bankAddress ? trimToMaxLength(values.bankAddress, 255) : values.bankAddress,
    accountName: values.accountName ? trimToMaxLength(values.accountName, 128) : values.accountName,
    bankAccount: values.bankAccount ? trimToMaxLength(values.bankAccount, 64) : values.bankAccount,
    bankCode: values.bankCode ? trimToMaxLength(values.bankCode, 32) : values.bankCode,
    codeSort: values.codeSort ? trimToMaxLength(values.codeSort, 32) : values.codeSort,
  }
}

export const CreateBankAccountForm = withFormik<OuterProps, CreateBankAccountFormValues>({
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(enforceMaxLength(values))
    } finally {
      setSubmitting(false)
    }
  },

  validate: (values, { validationRules }) => {
    const errors: FormikErrors<CreateBankAccountFormValues> = {}
    const requiredKeys: (keyof CreateBankAccountFormValues)[] = [
      'bankName',
      'bankAddress',
      'accountName',
      'bankAccount',
      'bankCode',
    ]

    if (validationRules) {
      if (validationRules.bankNameValidation.regex) {
        if (values.bankName) {
          if (!new RegExp(validationRules.bankNameValidation.regex).test(values.bankName)) {
            errors.bankName = t('Validation.Invalid format')
          }
        }
      }

      if (validationRules.bankAddressValidation.regex) {
        if (values.bankAddress) {
          if (!new RegExp(validationRules.bankAddressValidation.regex).test(values.bankAddress)) {
            errors.bankAddress = t('Validation.Invalid format')
          }
        }
      }

      if (validationRules.accountNameValidation.regex) {
        if (values.accountName) {
          if (!new RegExp(validationRules.accountNameValidation.regex).test(values.accountName)) {
            errors.accountName = t('Validation.Invalid format')
          }
        }
      }

      if (validationRules.bankCodeValidation.regex) {
        if (values.bankCode) {
          if (!new RegExp(validationRules.bankCodeValidation.regex).test(values.bankCode)) {
            errors.bankCode = t('Validation.Invalid format')
          }
        }
      }

      if (validationRules.bankCodeSortValidation.regex) {
        if (values.codeSort) {
          if (!new RegExp(validationRules.bankCodeSortValidation.regex).test(values.codeSort)) {
            errors.codeSort = t('Validation.Invalid format')
          }
        }
      }

      if (validationRules.bankAccountValidation.regex) {
        if (values.bankAccount) {
          if (!new RegExp(validationRules.bankAccountValidation.regex).test(values.bankAccount)) {
            errors.bankAccount = t('Validation.Invalid format')
          }
        }
      }
    }

    requiredKeys.forEach((key) => {
      if (values[key]) {
        if (typeof values[key] === 'string') {
          if (!(values[key] as string)?.trim()) {
            errors[key] = t('Validation.Required')
          }
        }
      } else {
        errors[key] = t('Validation.Required')
      }
    })

    if (!values.document && !values.previouslyUploadedDocumentId) {
      errors.document = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(CreateBankAccountFormUI)
